import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

export interface ApiVoucherItem {
    id: string;
    code: string;
    name: string;
    description: string;
    type: 'percentage' | 'fixed';
    value: string | number;
    max_usage?: number;
    max_usage_per_user?: number;
    min_purchase?: string | number;
    max_discount?: string | number;
    applicable_to?: string;
    applicable_items?: any[];
    start_date?: string;
    end_date?: string;
    is_active?: boolean;
    is_valid?: boolean;
    created_at?: string;
}

export interface VoucherDisplayItem extends ApiVoucherItem {
    discountLabel: string;
    minPurchaseFormatted: string;
    maxDiscountFormatted: string;
    validUntilFormatted: string;
    isClaimed: boolean;
    showTerms: boolean;
}

@Component({
    selector: 'app-voucher',
    standalone: true,
    imports: [CommonModule, FormsModule, RouterModule],
    templateUrl: './voucher.component.html',
    styleUrls: ['./voucher.component.scss']
})
export class VoucherComponent implements OnInit {
    isLoading: boolean = false;
    errorMessage: string | null = null;

    // Filters
    searchQuery: string = '';
    selectedTab: 'all' | 'percentage' | 'fixed' | 'claimed' = 'all';

    // Voucher List
    vouchers: VoucherDisplayItem[] = [];

    // Claim Modal State
    isClaimModalOpen: boolean = false;
    selectedVoucherForClaim: VoucherDisplayItem | null = null;
    claimEmail: string = '';
    isClaiming: boolean = false;

    // Validation Calculator State
    calculatorCode: string = '';
    calculatorEmail: string = '';
    calculatorPrice: number = 150000;
    calculatorPackageId: string = 'cf884a1a-9a84-4dc2-b114-3777d30b36ef';
    isValidating: boolean = false;
    validationResult: any = null;
    validationError: string | null = null;

    // Toast state
    copiedToastCode: string | null = null;

    // Featured Promo Banner from Showcases (promo_banner)
    promoBannerUrl: string = '';
    promoBanner: any = null;

    constructor(
        private apiService: ApiService,
        public router: Router,
        private cdr: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.loadSavedEmail();
        this.fetchVouchers();
        this.fetchPromoBanner();
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, behavior: 'instant' });
        }
    }

    private loadSavedEmail(): void {
        if (typeof window !== 'undefined') {
            const savedEmail = localStorage.getItem('user_voucher_email');
            if (savedEmail) {
                this.claimEmail = savedEmail;
                this.calculatorEmail = savedEmail;
            }
        }
    }

    getClaimedVoucherCodes(): string[] {
        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem('claimed_voucher_codes');
            if (stored) {
                try {
                    return JSON.parse(stored);
                } catch (e) {
                    return [];
                }
            }
        }
        return [];
    }

    private saveClaimedVoucherCode(code: string): void {
        if (typeof window !== 'undefined' && code) {
            const current = this.getClaimedVoucherCodes();
            if (!current.includes(code.toUpperCase())) {
                current.push(code.toUpperCase());
                localStorage.setItem('claimed_voucher_codes', JSON.stringify(current));
            }
        }
    }

    async fetchVouchers(): Promise<void> {
        this.isLoading = true;
        this.errorMessage = null;

        try {
            const apiEndpoint = '/api/v1/front/public/vouchers/list?paginate=false&search=&page=&per_page=';
            let response: any = await this.apiService.get(apiEndpoint, false);

            if (!response || !response.success || !response.data?.list) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`);
                    if (devRes.ok) {
                        response = await devRes.json();
                    }
                } catch (e) {
                    console.error('Direct fetch vouchers fallback error:', e);
                }
            }

            if (response && response.data && Array.isArray(response.data.list)) {
                const claimedCodes = this.getClaimedVoucherCodes();
                this.vouchers = response.data.list.map((item: ApiVoucherItem) => this.mapVoucherItem(item, claimedCodes));
            } else {
                this.vouchers = [];
            }
        } catch (error) {
            console.error('Error fetching vouchers:', error);
            this.vouchers = [];
            this.errorMessage = 'Gagal memuat daftar voucher. Silakan muat ulang halaman.';
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    private mapVoucherItem(item: ApiVoucherItem, claimedCodes: string[]): VoucherDisplayItem {
        const valNum = Number(item.value || 0);
        let discountLabel = '';

        if (item.type === 'percentage') {
            discountLabel = `${Math.round(valNum)}% OFF`;
        } else {
            discountLabel = `Rp ${this.formatNumber(valNum)} OFF`;
        }

        const minPurchaseNum = Number(item.min_purchase || 0);
        const minPurchaseFormatted = minPurchaseNum > 0 ? `Rp ${this.formatNumber(minPurchaseNum)}` : 'Tanpa Min. Belanja';

        const maxDiscountNum = Number(item.max_discount || 0);
        const maxDiscountFormatted = maxDiscountNum > 0 ? `Rp ${this.formatNumber(maxDiscountNum)}` : 'Tanpa Batas Maksimal';

        let validUntilFormatted = 'Selama Persediaan Ada';
        if (item.end_date) {
            try {
                const d = new Date(item.end_date);
                const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
                validUntilFormatted = `s/d ${d.toLocaleDateString('id-ID', options)}`;
            } catch (e) {
                validUntilFormatted = item.end_date;
            }
        }

        const isClaimed = claimedCodes.includes(item.code.toUpperCase());

        return {
            ...item,
            discountLabel,
            minPurchaseFormatted,
            maxDiscountFormatted,
            validUntilFormatted,
            isClaimed,
            showTerms: false
        };
    }

    get filteredVouchers(): VoucherDisplayItem[] {
        return this.vouchers.filter(v => {
            // Search query filter
            const matchesQuery = !this.searchQuery ||
                v.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                v.code.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                v.description.toLowerCase().includes(this.searchQuery.toLowerCase());

            // Tab filter
            let matchesTab = true;
            if (this.selectedTab === 'percentage') {
                matchesTab = v.type === 'percentage';
            } else if (this.selectedTab === 'fixed') {
                matchesTab = v.type === 'fixed';
            } else if (this.selectedTab === 'claimed') {
                matchesTab = v.isClaimed;
            }

            return matchesQuery && matchesTab;
        });
    }

    toggleTerms(voucher: VoucherDisplayItem, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        voucher.showTerms = !voucher.showTerms;
    }

    copyCode(code: string, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }

        if (typeof navigator !== 'undefined' && navigator.clipboard) {
            navigator.clipboard.writeText(code);
        }

        this.copiedToastCode = code;
        setTimeout(() => {
            if (this.copiedToastCode === code) {
                this.copiedToastCode = null;
                this.cdr.detectChanges();
            }
        }, 2500);

        const Toast = Swal.mixin({
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
            didOpen: (toast) => {
                toast.addEventListener('mouseenter', Swal.stopTimer);
                toast.addEventListener('mouseleave', Swal.resumeTimer);
            }
        });

        Toast.fire({
            icon: 'success',
            title: `Kode ${code} Berhasil Disalin!`
        });
    }

    openClaimModal(voucher: VoucherDisplayItem, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.selectedVoucherForClaim = voucher;
        this.isClaimModalOpen = true;
    }

    closeClaimModal(): void {
        this.isClaimModalOpen = false;
        this.selectedVoucherForClaim = null;
    }

    async submitClaimVoucher(): Promise<void> {
        if (!this.claimEmail || !this.claimEmail.includes('@')) {
            Swal.fire({
                icon: 'warning',
                title: 'Email Tidak Valid',
                text: 'Harap masukkan alamat email yang valid untuk mengklaim voucher.',
                confirmButtonColor: '#024D3D'
            });
            return;
        }

        if (!this.selectedVoucherForClaim) return;

        this.isClaiming = true;
        const voucherCode = this.selectedVoucherForClaim.code;

        // Save email to localStorage for convenience
        if (typeof window !== 'undefined') {
            localStorage.setItem('user_voucher_email', this.claimEmail);
        }

        try {
            const apiEndpoint = '/api/v1/front/public/vouchers/claim';
            const payload = {
                code: voucherCode,
                email: this.claimEmail.trim()
            };

            let response: any = await this.apiService.post(apiEndpoint, payload, false);

            if (!response || !response.success) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    });
                    if (devRes.ok) {
                        response = await devRes.json();
                    } else {
                        const errBody = await devRes.json();
                        response = errBody;
                    }
                } catch (e) {
                    console.error('Direct claim fallback error:', e);
                }
            }

            if (response && response.success) {
                this.saveClaimedVoucherCode(voucherCode);
                this.selectedVoucherForClaim.isClaimed = true;
                this.closeClaimModal();

                Swal.fire({
                    icon: 'success',
                    title: 'Voucher Berhasil Diklaim! 🎉',
                    html: `Voucher <b>${voucherCode}</b> telah tersimpan di akun/email Anda (<b>${this.claimEmail}</b>). Gunakan saat melakukan pemesanan!`,
                    showCancelButton: true,
                    confirmButtonText: 'Gunakan Sekarang',
                    cancelButtonText: 'Tutup',
                    confirmButtonColor: '#024D3D',
                    cancelButtonColor: '#6B7280'
                }).then((result) => {
                    if (result.isConfirmed) {
                        this.useVoucher(voucherCode);
                    }
                });
            } else {
                const msg = response?.message || 'Gagal mengklaim voucher. Anda mungkin telah mencapai batas kuota klaim.';
                Swal.fire({
                    icon: 'info',
                    title: 'Informasi Klaim',
                    text: msg,
                    confirmButtonColor: '#024D3D'
                });
            }
        } catch (error: any) {
            console.error('Error claiming voucher:', error);
            Swal.fire({
                icon: 'error',
                title: 'Terjadi Kesalahan',
                text: 'Koneksi bermasalah. Silakan coba kembali beberapa saat lagi.',
                confirmButtonColor: '#024D3D'
            });
        } finally {
            this.isClaiming = false;
            this.cdr.detectChanges();
        }
    }

    useVoucher(code: string, event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.copyCode(code);

        // Store selected voucher for auto-apply in checkout
        if (typeof window !== 'undefined') {
            localStorage.setItem('active_promo_code', code);
        }

        // Navigate to Package or Invitation page
        this.router.navigate(['/package']);
    }

    openPromoBannerLink(): void {
        if (this.promoBanner?.link_url) {
            if (this.promoBanner.link_url.startsWith('http')) {
                window.open(this.promoBanner.link_url, '_blank');
            } else {
                this.router.navigate([this.promoBanner.link_url]);
            }
        } else {
            this.useVoucher('WELCOME10');
        }
    }

    async fetchPromoBanner(): Promise<void> {
        try {
            const apiEndpoint = '/api/v1/front/public/showcases/list?type=promo_banner';
            let response: any = await this.apiService.get(apiEndpoint, false);

            if (!response || !response.success || !response.data?.list) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`);
                    if (devRes.ok) {
                        response = await devRes.json();
                    }
                } catch (e) {
                    console.error('Direct fetch promo banner fallback error:', e);
                }
            }

            let rawList: any[] = [];
            if (response && response.data) {
                if (Array.isArray(response.data.list)) {
                    rawList = response.data.list;
                } else if (Array.isArray(response.data)) {
                    rawList = response.data;
                } else if (Array.isArray(response.data.data)) {
                    rawList = response.data.data;
                }
            } else if (Array.isArray(response?.list)) {
                rawList = response.list;
            }

            if (rawList && rawList.length > 0) {
                const activeBanners = rawList
                    .filter((item: any) => item.is_active !== false && item.asset?.url)
                    .sort((a: any, b: any) => {
                        const orderA = a.sort_order !== undefined && a.sort_order !== null && a.sort_order !== '' ? Number(a.sort_order) : 9999;
                        const orderB = b.sort_order !== undefined && b.sort_order !== null && b.sort_order !== '' ? Number(b.sort_order) : 9999;
                        if (orderA !== orderB) return orderA - orderB;
                        return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
                    });

                if (activeBanners.length > 0) {
                    this.promoBanner = activeBanners[0];
                    this.promoBannerUrl = activeBanners[0].asset.url;
                }
            }
        } catch (error) {
            console.error('Error fetching promo banner in voucher page:', error);
        } finally {
            this.cdr.detectChanges();
        }
    }

    async validateVoucher(): Promise<void> {
        if (!this.calculatorCode) {
            this.validationError = 'Harap masukkan kode voucher.';
            return;
        }

        if (!this.calculatorEmail || !this.calculatorEmail.includes('@')) {
            this.validationError = 'Harap masukkan email aktif Anda.';
            return;
        }

        this.isValidating = true;
        this.validationError = null;
        this.validationResult = null;

        try {
            const apiEndpoint = '/api/v1/front/public/vouchers/validate';
            const payload = {
                code: this.calculatorCode.trim().toUpperCase(),
                package_id: this.calculatorPackageId,
                price: Number(this.calculatorPrice) || 150000,
                email: this.calculatorEmail.trim()
            };

            let response: any = await this.apiService.post(apiEndpoint, payload, false);

            if (!response || !response.success) {
                try {
                    const devRes = await fetch(`https://dev.api.visideastudio.com${apiEndpoint}`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload)
                    });
                    if (devRes.ok) {
                        response = await devRes.json();
                    } else {
                        response = await devRes.json();
                    }
                } catch (e) {
                    console.error('Validate voucher fallback error:', e);
                }
            }

            if (response && response.success && response.data) {
                this.validationResult = response.data;
            } else {
                this.validationError = response?.message || 'Kode voucher tidak valid atau tidak memenuhi syarat transaksi.';
            }
        } catch (error: any) {
            console.error('Error validating voucher:', error);
            this.validationError = 'Gagal memvalidasi voucher. Periksa koneksi internet Anda.';
        } finally {
            this.isValidating = false;
            this.cdr.detectChanges();
        }
    }

    fillCalculator(voucher: VoucherDisplayItem): void {
        this.calculatorCode = voucher.code;
        const minP = Number(voucher.min_purchase || 0);
        if (minP > 0) {
            this.calculatorPrice = minP;
        }
        
        // Scroll to calculator
        if (typeof document !== 'undefined') {
            const el = document.getElementById('voucher-calculator-section');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }
    }

    formatNumber(val: number | string): string {
        const n = Number(val) || 0;
        return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    }
}
